import { PowerAmounts } from "components/detailPane/BusDetailPane/BusDetailPane";
import {
    GetQueueProjectHopsQuery,
    GetRelatedQueueProjectsQuery
} from "generated/graphql";
import { convertMaybeToUndefined } from "graphql/helpers/queryHelpers";
import {
    CHARGING_RESOURCE_TYPE,
    ENERGY_RESOURCE_TYPE,
    isKnownResourceType,
    ResourceType
} from "./resourceType";

/**
 * One per-constraint contribution row that fed into a project's score.
 * Kept around so the drilldown drawer can show which constraints a project
 * shares with the study bus without a refetch.
 */
export type ProjectConstraintContribution = {
    readonly monitoredFacilityId: string;
    readonly monitoredFacilityName: string;
    readonly contingency: string;
    readonly resourceType: ResourceType;
    readonly projectDfax: number;
    readonly contribution: number;
};

/**
 * A single queue project's contribution on one specific constraint row,
 * used for the per-constraint drilldown in the bus constraint table.
 * `numHops` and `isInCurrentScope` are `undefined` when the respective
 * lookup is unavailable (still loading or not deployed), as opposed to a
 * definitive "no hops entry" / "not in scope" answer.
 */
export type RelatedProjectOnConstraint = {
    readonly projectNumber: string;
    readonly fuelType: string;
    readonly poiDescription: string;
    readonly subRegion: string;
    readonly cluster: string | undefined;
    readonly status: string | undefined;
    readonly busId: string | undefined;
    readonly facilityId: string | undefined;
    readonly erisCapacityMw: number;
    readonly nrisCapacityMw: number | undefined;
    readonly projectDfax: number;
    readonly contribution: number;
    // The project's total relatedness score summed across every
    // constraint (not just this one) — the same number that ranks the
    // related-projects sidebar, so "impact" reads consistently.
    readonly overallScore: number;
    readonly numHops: number | undefined;
    readonly isInCurrentScope: boolean | undefined;
};

// Shared labels for marking a queue project's membership in the currently
// selected scope, used by both the related-projects sidebar and the
// per-constraint expand panel so the two read identically. Not-in-scope is
// the marked state: those projects are modeled at 0 MW, so their generation
// is absent from the constraint loadings the page displays.
export const IN_SCOPE_LABEL = "In scope";
export const NOT_IN_SCOPE_LABEL = "Not in scope";

/**
 * Stable key identifying one constraint row, matched across the constraints
 * query (`Constraint.monitoredFacilityId/contingency/resourceType`) and the
 * relatedness query (`constraint_stack.monitored_facility_id/contingency/
 * resource_type`). Used as the map key for per-constraint project lookups.
 */
export const relatedProjectsConstraintKey = (
    monitoredFacilityId: string,
    contingency: string,
    resourceType: ResourceType
): string => `${monitoredFacilityId}|${contingency}|${resourceType}`;

export type RelatedProjectsByConstraint = ReadonlyMap<
    string,
    ReadonlyArray<RelatedProjectOnConstraint>
>;

/**
 * One queue project's aggregate relatedness against the current study bus,
 * computed at a specific (energy, capacity, charging) slider configuration.
 * `score` is the unsigned sum of all per-constraint contributions, so
 * projects that are highly entangled with the study bus rank highest
 * regardless of whether they push or relieve any individual constraint.
 */
export type RelatedQueueProject = {
    readonly projectNumber: string;
    readonly fuelType: string;
    readonly poiDescription: string;
    readonly subRegion: string;
    readonly cluster: string | undefined;
    readonly status: string | undefined;
    readonly erisCapacityMw: number;
    readonly nrisCapacityMw: number | undefined;
    readonly busId: string | undefined;
    readonly facilityId: string | undefined;
    readonly score: number;
    readonly contributions: ReadonlyArray<ProjectConstraintContribution>;
};

const PHASE_STATUS_LABELS: { readonly [key: string]: string } = {
    one: "Ph. 1",
    two: "Ph. 2",
    three: "Ph. 3",
    four: "Ph. 4",
    gia: "GIA"
};

export const formatPhaseStatus = (status: string): string => {
    return PHASE_STATUS_LABELS[status.toLowerCase()] ?? status;
};

/**
 * Display formatter for queue project `fuel_type` values. Shortens
 * verbose labels (e.g. "Battery Storage" -> "Battery") to keep dense
 * relatedness tables readable. Unknown values pass through unchanged
 * so we don't accidentally hide new fuel types added to the queue.
 *
 * NOTE: this is shared with the In-Queue app (ag-grid fuel-type cell
 * renderer) — do not change its output without sign-off there. The bus
 * page uses `formatFuelTypeForBusPage` below for richer formatting.
 */
const FUEL_TYPE_LABELS: { readonly [key: string]: string } = {
    "battery storage": "Battery"
};

export const formatFuelType = (fuelType: string): string => {
    return FUEL_TYPE_LABELS[fuelType.toLowerCase()] ?? fuelType;
};

/**
 * Bus-page-only fuel type formatter. Extends the shared labels with
 * WECC's snake_case enums (e.g. "natural_gas") and humanizes anything
 * unmapped so raw enum values never render on the bus page. Kept
 * separate from `formatFuelType` so In-Queue app text is untouched.
 */
const BUS_PAGE_FUEL_TYPE_LABELS: { readonly [key: string]: string } = {
    ...FUEL_TYPE_LABELS,
    battery_storage: "Battery",
    natural_gas: "Natural Gas",
    // Acronyms that generic title-casing would mangle ("Bess", "Pv", "Chp").
    bess: "BESS",
    pv: "Solar",
    chp: "CHP"
};

export const formatFuelTypeForBusPage = (fuelType: string): string => {
    const override = BUS_PAGE_FUEL_TYPE_LABELS[fuelType.toLowerCase()];
    if (override !== undefined) {
        return override;
    }
    // Values with any uppercase (e.g. MISO's display-ready "Solar") pass
    // through unchanged so we don't mangle intentional casing. All-lowercase
    // values are treated as raw enums (WECC snake_case like "hybrid" or
    // "pumped_storage") and humanized: underscores to spaces, words
    // title-cased. Unknown future values degrade to a readable label
    // instead of the raw enum.
    if (fuelType !== fuelType.toLowerCase()) {
        return fuelType;
    }
    return fuelType
        .split(/[_\s]+/)
        .filter((word) => word.length > 0)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
};

/**
 * Bucketing of contribution scores into qualitative tiers. The cutoffs
 * were picked so that bucket shares stay roughly stable across study
 * sizes (only the Dominant tier shifts meaningfully with bigger
 * injections, which is the intended signal). Edges are inclusive on
 * the lower bound, exclusive on the upper, so each score maps to
 * exactly one bucket.
 */
export const SCORE_BUCKET_TYPES = [
    "negligible",
    "low",
    "medium",
    "high",
    "dominant"
] as const;
export type ScoreBucketType = (typeof SCORE_BUCKET_TYPES)[number];

export const SCORE_BUCKET_LABEL: { readonly [K in ScoreBucketType]: string } = {
    negligible: "Negligible",
    low: "Low",
    medium: "Medium",
    high: "High",
    dominant: "Dominant"
};

/**
 * Ordinal rank per bucket (1 = most severe → 5 = least severe). Used
 * to prefix CSV-export values so Excel's default ascending sort puts
 * the highest-contribution projects at the top — matching the common
 * "click column header to see the most important rows first" workflow.
 */
export const SCORE_BUCKET_SORT_ORDER: {
    readonly [K in ScoreBucketType]: number;
} = {
    dominant: 1,
    high: 2,
    medium: 3,
    low: 4,
    negligible: 5
};

export const getScoreBucket = (score: number): ScoreBucketType => {
    // Guard against NaN / non-finite scores. Without this, NaN would
    // fall through every `score < X` check (NaN comparisons are always
    // false) and incorrectly bucket as "dominant". Treat any
    // unrepresentable score as "negligible" so a data glitch surfaces
    // as background noise rather than a falsely-alarming Dominant tag.
    if (!Number.isFinite(score)) {
        return "negligible";
    }
    if (score < 0.005) {
        return "negligible";
    }
    if (score < 0.025) {
        return "low";
    }
    if (score < 0.1) {
        return "medium";
    }
    if (score < 1.0) {
        return "high";
    }
    return "dominant";
};

const sliderMwForResourceType = (
    resourceType: ResourceType,
    powerAmounts: PowerAmounts
): number => {
    if (resourceType === ENERGY_RESOURCE_TYPE) {
        return powerAmounts.energySize;
    }
    if (resourceType === CHARGING_RESOURCE_TYPE) {
        return powerAmounts.chargingSize;
    }
    return powerAmounts.capacitySize;
};

type ConstraintStackRow =
    GetRelatedQueueProjectsQuery["constraint_stacks"][number];
type FacilityDfaxRow =
    ConstraintStackRow["queue_project_facility_dfaxes"][number];

type Accumulator = {
    readonly project: NonNullable<FacilityDfaxRow["queue_project"]>;
    readonly contributions: Array<ProjectConstraintContribution>;
    score: number;
};

/**
 * Validates the shared per-row inputs of the contribution formula and
 * computes the derived values both converters need. Returns undefined
 * (after logging a warning) for rows with an unknown resource_type or a
 * non-positive rating — bad backend data is skipped rather than fed
 * into the score math, where an unknown resource type would throw and a
 * rating of 0 would produce an Infinity/NaN contribution.
 */
const validateConstraintStackRow = (
    cs: ConstraintStackRow,
    powerAmounts: PowerAmounts
):
    | { readonly resourceType: ResourceType; readonly dynamicThreshold: number }
    | undefined => {
    if (!isKnownResourceType(cs.resource_type)) {
        console.warn(
            `Skipping constraint stack row for ${cs.monitored_facility_id} with unsupported resource type: ${cs.resource_type}`
        );
        return undefined;
    }
    if (cs.rating <= 0) {
        console.warn(
            `Skipping constraint stack row for ${cs.monitored_facility_id} with non-positive rating: ${cs.rating}`
        );
        return undefined;
    }
    const injectionMw = sliderMwForResourceType(cs.resource_type, powerAmounts);
    return {
        resourceType: cs.resource_type,
        dynamicThreshold: Math.max(cs.injection_threshold - injectionMw, 1)
    };
};

/**
 * Aggregates per-(constraint, project) DFAX rows from the relatedness query
 * into one `RelatedQueueProject` per project_number, using the per-row
 * `resource_type` to pick the matching project-size slider value.
 *
 * Per-row contribution (per the relatedness spec):
 *
 *     contribution = pmax * |project_dfax|
 *                    / rating
 *                    / max(injection_threshold - injection_mw, 1)
 *
 * Returns the list sorted by total contribution score descending so the
 * most entangled projects come first.
 *
 * NOTE: `pmax` is `eris_capacity_mw` for all resource types (incl. charging).
 * The MISO DFAX pipeline currently exposes only ERIS; revisit if a project
 * charging MW becomes available.
 */
export const convertHasuraDataToRelatedQueueProjects = (
    data: GetRelatedQueueProjectsQuery,
    powerAmounts: PowerAmounts
): ReadonlyArray<RelatedQueueProject> => {
    const byProject = new Map<string, Accumulator>();

    for (const cs of data.constraint_stacks) {
        const rowInputs = validateConstraintStackRow(cs, powerAmounts);
        if (rowInputs === undefined) {
            continue;
        }
        const { resourceType, dynamicThreshold } = rowInputs;

        for (const pfd of cs.queue_project_facility_dfaxes) {
            const queueProject = pfd.queue_project;
            if (queueProject === null || queueProject === undefined) {
                continue;
            }
            const pmax = queueProject.eris_capacity_mw;
            if (pmax <= 0) {
                continue;
            }
            const contribution =
                (pmax * Math.abs(pfd.dfax)) / cs.rating / dynamicThreshold;

            let entry = byProject.get(pfd.project_number);
            if (entry === undefined) {
                entry = {
                    project: queueProject,
                    contributions: [],
                    score: 0
                };
                byProject.set(pfd.project_number, entry);
            }
            entry.score += contribution;
            entry.contributions.push({
                monitoredFacilityId: cs.monitored_facility_id,
                monitoredFacilityName: cs.monitored_facility_name,
                contingency: cs.contingency,
                resourceType,
                projectDfax: pfd.dfax,
                contribution
            });
        }
    }

    const projects: Array<RelatedQueueProject> = [];
    byProject.forEach((entry, projectNumber) => {
        projects.push({
            projectNumber,
            fuelType: entry.project.fuel_type,
            poiDescription: entry.project.poi_description,
            subRegion: entry.project.sub_region,
            cluster: convertMaybeToUndefined(entry.project.cluster),
            status: convertMaybeToUndefined(entry.project.status),
            erisCapacityMw: entry.project.eris_capacity_mw,
            nrisCapacityMw: convertMaybeToUndefined(
                entry.project.nris_capacity_mw
            ),
            busId: convertMaybeToUndefined(entry.project.bus_id),
            facilityId: convertMaybeToUndefined(entry.project.facility_id),
            score: entry.score,
            contributions: entry.contributions
        });
    });

    return projects.sort((a, b) => {
        const scoreDiff = b.score - a.score;
        if (scoreDiff !== 0) {
            return scoreDiff;
        }
        return a.projectNumber.localeCompare(b.projectNumber);
    });
};

/**
 * A row in the related-projects table once impact (relatedness score) and
 * network proximity (hops) have been merged. A project can appear because it
 * shares constraints with the study bus (`score` defined), because it sits
 * within the studied hop radius (`numHops` defined), or both. The missing
 * metric is left `undefined` and rendered as an em-dash.
 */
export type RelatedQueueProjectRow = Omit<RelatedQueueProject, "score"> & {
    readonly score: number | undefined;
    readonly numHops: number | undefined;
};

/**
 * The related-projects table plus which metrics are present across the
 * whole dataset. The display column set and sort key are driven by these
 * flags: impact-only sorts by score, hops-only sorts by hops, and when both
 * are present we sort by impact then hops.
 */
export type RelatedQueueProjectsTable = {
    readonly rows: ReadonlyArray<RelatedQueueProjectRow>;
    readonly hasImpact: boolean;
    readonly hasHops: boolean;
};

type HopsRow = GetQueueProjectHopsQuery["queue_project_hops"][number];
type HopsQueueProject = NonNullable<HopsRow["queue_project"]>;

/**
 * Orders rows for display:
 *   - impact present  -> score descending (most entangled first), and when
 *     both metrics are present, hops ascending breaks ties;
 *   - hops only       -> hops ascending (electrically closest first).
 * Rows missing the active metric sink to the bottom (score -> -Infinity so
 * hops-only rows trail the scored ones; hops -> +Infinity so impact-only rows
 * trail the ones with a hop count). Project number is the final tiebreak.
 */
export const compareRelatedRows = (
    a: Pick<RelatedQueueProjectRow, "projectNumber" | "score" | "numHops">,
    b: Pick<RelatedQueueProjectRow, "projectNumber" | "score" | "numHops">,
    hasImpact: boolean,
    hasHops: boolean
): number => {
    if (hasImpact && (a.score !== undefined || b.score !== undefined)) {
        const scoreDiff = (b.score ?? -Infinity) - (a.score ?? -Infinity);
        if (scoreDiff !== 0) {
            return scoreDiff;
        }
    }
    if (hasHops && (a.numHops !== undefined || b.numHops !== undefined)) {
        const hopsDiff = (a.numHops ?? Infinity) - (b.numHops ?? Infinity);
        if (hopsDiff !== 0) {
            return hopsDiff;
        }
    }
    return a.projectNumber.localeCompare(b.projectNumber);
};

const hopsRowToRelatedRow = (
    projectNumber: string,
    numHops: number,
    project: HopsQueueProject
): RelatedQueueProjectRow => ({
    projectNumber,
    fuelType: project.fuel_type,
    poiDescription: project.poi_description,
    subRegion: project.sub_region,
    cluster: convertMaybeToUndefined(project.cluster),
    status: convertMaybeToUndefined(project.status),
    erisCapacityMw: project.eris_capacity_mw,
    nrisCapacityMw: convertMaybeToUndefined(project.nris_capacity_mw),
    busId: convertMaybeToUndefined(project.bus_id),
    facilityId: convertMaybeToUndefined(project.facility_id),
    score: undefined,
    numHops,
    contributions: []
});

/**
 * Merges the impact-scored related projects with the queue-project hops
 * data into a single table keyed by project number. Projects present in only
 * one source still get a row, with the absent metric left `undefined`.
 * `hopsData` is `undefined` when the hops query is unavailable (e.g. the table
 * isn't deployed in the current environment), in which case the result is the
 * impact-only table.
 */
export const mergeRelatedProjectsAndHops = (
    impactProjects: ReadonlyArray<RelatedQueueProject>,
    hopsData: GetQueueProjectHopsQuery | undefined
): RelatedQueueProjectsTable => {
    const hopsByProject = new Map<string, number>();
    const hopsProjectByNumber = new Map<string, HopsQueueProject>();
    if (hopsData !== undefined) {
        for (const row of hopsData.queue_project_hops) {
            // The table grain is one row per (study bus, scope_view, region,
            // project), so project_number is unique within this query.
            hopsByProject.set(row.project_number, row.num_hops);
            if (row.queue_project !== null && row.queue_project !== undefined) {
                hopsProjectByNumber.set(row.project_number, row.queue_project);
            }
        }
    }

    const rowByProject = new Map<string, RelatedQueueProjectRow>();

    // Impact-scored projects carry full metadata and per-constraint
    // contributions; attach a hop count when the project also has one.
    for (const project of impactProjects) {
        rowByProject.set(project.projectNumber, {
            ...project,
            numHops: hopsByProject.get(project.projectNumber)
        });
    }

    // Projects that only have a hop count (no shared constraints) still get a
    // row, built from the hops query's queue_project metadata.
    hopsByProject.forEach((numHops, projectNumber) => {
        if (rowByProject.has(projectNumber)) {
            return;
        }
        const project = hopsProjectByNumber.get(projectNumber);
        if (project === undefined) {
            return;
        }
        rowByProject.set(
            projectNumber,
            hopsRowToRelatedRow(projectNumber, numHops, project)
        );
    });

    const hasImpact = impactProjects.length > 0;
    const hasHops = hopsByProject.size > 0;

    const rows = Array.from(rowByProject.values()).sort((a, b) =>
        compareRelatedRows(a, b, hasImpact, hasHops)
    );

    return { rows, hasImpact, hasHops };
};

/**
 * Flattens the hops query into a project_number -> num_hops lookup.
 * The table grain is one row per (study bus, scope_view, region, project),
 * so project_number is unique within one query result.
 */
export const buildNumHopsByProject = (
    hopsData: GetQueueProjectHopsQuery
): ReadonlyMap<string, number> => {
    const map = new Map<string, number>();
    for (const row of hopsData.queue_project_hops) {
        map.set(row.project_number, row.num_hops);
    }
    return map;
};

/**
 * Fixed slider configuration used to compute relatedness scores when re-studying
 * a bus. The user explicitly asked for energy = capacity = 500 MW; charging
 * follows for consistency (only affects storage `charging` rows).
 */
export const RESTUDY_IMPACT_POWER_AMOUNTS: PowerAmounts = {
    energySize: 500,
    capacitySize: 500,
    chargingSize: 500
};

export interface QueueProjectRelatedness {
    readonly scoreByProjectNumber: ReadonlyMap<string, number>;
    readonly hopsByProjectNumber: ReadonlyMap<string, number>;
}

export const toQueueProjectRelatedness = (
    relatedData: GetRelatedQueueProjectsQuery | undefined,
    hopsData: GetQueueProjectHopsQuery | undefined
): QueueProjectRelatedness => ({
    scoreByProjectNumber:
        relatedData === undefined
            ? new Map()
            : new Map(
                  convertHasuraDataToRelatedQueueProjects(
                      relatedData,
                      RESTUDY_IMPACT_POWER_AMOUNTS
                  ).map((p) => [p.projectNumber, p.score])
              ),
    hopsByProjectNumber:
        hopsData === undefined ? new Map() : buildNumHopsByProject(hopsData)
});

/**
 * Transposes the same relatedness rows into a per-constraint view:
 *   constraintKey -> ranked list of queue projects sharing that constraint.
 *
 * Each entry's `contribution` uses the same formula as
 * `convertHasuraDataToRelatedQueueProjects` so the per-constraint score
 * adds up to the project-level totals shown in the side panel.
 *
 * Returned lists are sorted by `contribution` descending. Entries with
 * non-positive `pmax` or missing project metadata are dropped.
 *
 * `numHopsByProject` / `inCurrentScopeProjectNumbers` enrich each entry
 * with network proximity and scope membership; pass `undefined` while
 * those lookups are loading (or unavailable) and the fields stay
 * `undefined` rather than claiming an answer.
 */
export const convertHasuraDataToProjectsByConstraint = (
    data: GetRelatedQueueProjectsQuery,
    powerAmounts: PowerAmounts,
    numHopsByProject: ReadonlyMap<string, number> | undefined,
    inCurrentScopeProjectNumbers: ReadonlySet<string> | undefined
): RelatedProjectsByConstraint => {
    const byConstraint = new Map<string, Array<RelatedProjectOnConstraint>>();
    // Per-project totals across all constraints, accumulated during the
    // same pass and stamped onto every entry afterwards (an entry's
    // overallScore isn't final until all rows have been seen).
    const overallScoreByProject = new Map<string, number>();

    for (const cs of data.constraint_stacks) {
        const rowInputs = validateConstraintStackRow(cs, powerAmounts);
        if (rowInputs === undefined) {
            continue;
        }
        const { resourceType, dynamicThreshold } = rowInputs;
        const key = relatedProjectsConstraintKey(
            cs.monitored_facility_id,
            cs.contingency,
            resourceType
        );

        let bucket = byConstraint.get(key);
        if (bucket === undefined) {
            bucket = [];
            byConstraint.set(key, bucket);
        }

        for (const pfd of cs.queue_project_facility_dfaxes) {
            const queueProject = pfd.queue_project;
            if (queueProject === null || queueProject === undefined) {
                continue;
            }
            const pmax = queueProject.eris_capacity_mw;
            if (pmax <= 0) {
                continue;
            }
            const contribution =
                (pmax * Math.abs(pfd.dfax)) / cs.rating / dynamicThreshold;
            overallScoreByProject.set(
                pfd.project_number,
                (overallScoreByProject.get(pfd.project_number) ?? 0) +
                    contribution
            );
            bucket.push({
                projectNumber: pfd.project_number,
                fuelType: queueProject.fuel_type,
                poiDescription: queueProject.poi_description,
                subRegion: queueProject.sub_region,
                cluster: convertMaybeToUndefined(queueProject.cluster),
                status: convertMaybeToUndefined(queueProject.status),
                busId: convertMaybeToUndefined(queueProject.bus_id),
                facilityId: convertMaybeToUndefined(queueProject.facility_id),
                erisCapacityMw: pmax,
                nrisCapacityMw: convertMaybeToUndefined(
                    queueProject.nris_capacity_mw
                ),
                projectDfax: pfd.dfax,
                contribution,
                // Placeholder — finalized below once every row has been
                // accumulated into overallScoreByProject.
                overallScore: 0,
                numHops: numHopsByProject?.get(pfd.project_number),
                isInCurrentScope: inCurrentScopeProjectNumbers?.has(
                    pfd.project_number
                )
            });
        }
    }

    byConstraint.forEach((entries, key) => {
        const finalizedEntries = entries.map((entry) => ({
            ...entry,
            overallScore: overallScoreByProject.get(entry.projectNumber) ?? 0
        }));
        finalizedEntries.sort((a, b) => {
            const scoreDiff = b.contribution - a.contribution;
            if (scoreDiff !== 0) {
                return scoreDiff;
            }
            return a.projectNumber.localeCompare(b.projectNumber);
        });
        byConstraint.set(key, finalizedEntries);
    });

    return byConstraint;
};
